if (!customElements.get('site-lock')) {
  class SiteLock extends HTMLElement {
    constructor() {
      super();
    }

    // localStorage helper functions (doesn't require cookie consent)
    getStoredValue(key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.warn('Site Lock: localStorage not available', e);
        return null;
      }
    }

    setStoredValue(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        console.warn('Site Lock: localStorage not available', e);
      }
    }

    connectedCallback() {
      // Determine form type first (before any early returns)
      this.isKlaviyoForm = !!this.querySelector('[data-form-type="klaviyo"]');

      // Set up Klaviyo form listener IMMEDIATELY if using Klaviyo
      // This ensures we catch events even if the lock is hidden initially
      if (this.isKlaviyoForm) {
        this.setupKlaviyoForm();
      } else {
        this.form = this.querySelector('#site-lock-form');
        this.emailInput = this.querySelector('#SiteLockForm-email');
      }

      // Initialize countdown elements (needed for both live and preview)
      this.countdownElement = this.querySelector('.site-lock__countdown-value');
      this.countdownTimeElement = this.querySelector('.site-lock__countdown-time');

      // Check if we're in Shopify theme editor (design mode)
      this.isDesignMode = typeof window.Shopify !== 'undefined' && window.Shopify.designMode;

      // Set up Shopify theme editor listeners for preview
      if (this.isDesignMode) {
        this.setupEditorListeners();
        return; // Don't auto-show in editor, only show when section is selected
      }

      // Check if we're within the time window (if time restrictions are set)
      if (!this.isWithinTimeWindow()) {
        return;
      }

      // Check if form was successfully submitted (Shopify form posts to same page)
      const formSuccess = this.querySelector('[data-form-success="true"]');
      if (formSuccess) {
        this.setStoredValue('site-lock-submitted', 'true');
        return; // Don't show lock
      }

      // Check if user has already submitted email
      const hasSubmitted = this.getStoredValue('site-lock-submitted');
      if (hasSubmitted) {
        return;
      }

      // Show lock immediately and set up all interactions
      this.initializeLock();
    }

    setupEditorListeners() {
      // Theme editor: show lock when section is selected, hide when deselected
      this.shopifySectionSelectHandler = (e) => {
        if (!e.target.querySelector('site-lock')) {
          return;
        }
        this.showLock();
        this.startTimeWindowMonitor(); // Start countdown for preview
      };

      this.shopifySectionDeselectHandler = (e) => {
        if (!e.target.querySelector('site-lock')) {
          return;
        }
        this.hideLock();
      };

      document.addEventListener('shopify:section:select', this.shopifySectionSelectHandler);
      document.addEventListener('shopify:section:deselect', this.shopifySectionDeselectHandler);
    }

    initializeLock() {
      // Show the lock
      this.showLock();

      // Start monitoring time window (and update countdown if enabled)
      this.startTimeWindowMonitor();

      // Bind form submit event (Shopify forms only)
      if (!this.isKlaviyoForm && this.form) {
        this.form.addEventListener('submit', this.onShopifyFormSubmit.bind(this));
      }

      // Prevent clicks outside modal (no way to close)
      this.addEventListener('click', this.onModalClick.bind(this));
    }

    isWithinTimeWindow() {
      const startTimeStr = this.getAttribute('data-start-time');
      const endTimeStr = this.getAttribute('data-end-time');

      // If no time restrictions, always show lock
      if (!startTimeStr && !endTimeStr) {
        return true;
      }

      // Get current time as Unix timestamp (seconds since epoch)
      const nowTimestamp = Math.floor(Date.now() / 1000);

      // Check start time (Unix timestamp from Liquid)
      if (startTimeStr) {
        const startTimestamp = parseInt(startTimeStr, 10);
        if (nowTimestamp < startTimestamp) {
          return false;
        }
      }

      // Check end time (Unix timestamp from Liquid)
      if (endTimeStr) {
        const endTimestamp = parseInt(endTimeStr, 10);
        if (nowTimestamp > endTimestamp) {
          return false;
        }
      }

      return true;
    }

    startTimeWindowMonitor() {
      const startTimeStr = this.getAttribute('data-start-time');
      const endTimeStr = this.getAttribute('data-end-time');

      // Only monitor if we have time restrictions
      if (!startTimeStr && !endTimeStr) {
        return;
      }

      // Update countdown immediately
      this.updateCountdown();

      // Check every second if we have a countdown, otherwise every 10 seconds
      const intervalTime = this.countdownElement ? 1000 : 10000;

      this.timeWindowInterval = setInterval(() => {
        // Update countdown if it exists
        if (this.countdownElement) {
          this.updateCountdown();
        }

        // Check if time window has expired
        if (!this.isWithinTimeWindow()) {
          this.hideLock();
          this.stopTimeWindowMonitor();
        }
      }, intervalTime);
    }

    updateCountdown() {
      if (!this.countdownElement) return;

      const endTimeStr = this.getAttribute('data-end-time');
      if (!endTimeStr) return;

      const endTimestamp = parseInt(endTimeStr, 10);
      const nowTimestamp = Math.floor(Date.now() / 1000);
      const remainingSeconds = endTimestamp - nowTimestamp;

      if (remainingSeconds <= 0) {
        this.countdownElement.textContent = 'Expired';
        return;
      }

      // Calculate days, hours, minutes, seconds
      const days = Math.floor(remainingSeconds / 86400);
      const hours = Math.floor((remainingSeconds % 86400) / 3600);
      const minutes = Math.floor((remainingSeconds % 3600) / 60);
      const seconds = remainingSeconds % 60;

      // Format countdown string
      let countdownText = '';
      if (days > 0) {
        countdownText += `${days}d `;
      }
      if (hours > 0 || days > 0) {
        countdownText += `${hours}h `;
      }
      if (minutes > 0 || hours > 0 || days > 0) {
        countdownText += `${minutes}m `;
      }
      countdownText += `${seconds}s`;

      this.countdownElement.textContent = countdownText.trim();

      // Update datetime attribute for semantic HTML
      if (this.countdownTimeElement) {
        const endDate = new Date(endTimestamp * 1000);
        this.countdownTimeElement.setAttribute('datetime', endDate.toISOString());
      }
    }

    stopTimeWindowMonitor() {
      if (this.timeWindowInterval) {
        clearInterval(this.timeWindowInterval);
        this.timeWindowInterval = null;
      }
    }

    disconnectedCallback() {
      // Clean up when element is removed from DOM
      this.stopTimeWindowMonitor();

      // Remove body lock class in case element is removed while lock is active
      document.body.classList.remove('site-locked');

      // Clean up Shopify section listeners
      if (this.shopifySectionSelectHandler) {
        document.removeEventListener('shopify:section:select', this.shopifySectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.shopifySectionDeselectHandler);
      }
    }

    setupKlaviyoForm() {
      // Listen for Klaviyo form submit event
      // https://developers.klaviyo.com/en/docs/track_klaviyo_form_activity_using_javascript
      const klaviyoHandler = (e) => {
        // Only process submit events
        if (e.detail.type !== 'submit') {
          return;
        }

        // Check if already submitted (prevents duplicate processing)
        if (this.getStoredValue('site-lock-submitted')) {
          return;
        }

        // Set localStorage and hide lock
        this.setStoredValue('site-lock-submitted', 'true');
        this.hideLock();
      };

      window.addEventListener('klaviyoForms', klaviyoHandler);
    }

    showLock() {
      this.classList.add('is-visible');
      document.body.classList.add('site-locked');
    }

    hideLock() {
      this.classList.remove('is-visible');
      document.body.classList.remove('site-locked');
      this.stopTimeWindowMonitor();
    }

    onShopifyFormSubmit(e) {
      // Validate email (Shopify will also validate, but we do it client-side first)
      const email = this.emailInput.value.trim();

      if (!email || !this.isValidEmail(email)) {
        e.preventDefault();
        this.emailInput.focus();
        return;
      }

      // Form will submit normally and reload the page
      // When page reloads, the form.posted_successfully? will be true
      // and we'll set the localStorage then
    }

    onModalClick(e) {
      // Prevent any clicks on the overlay from propagating
      // Users must submit the form to unlock
      if (!e.target.closest('.site-lock__body')) {
        e.stopPropagation();
      }
    }

    isValidEmail(email) {
      // Simple email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }
  }

  customElements.define('site-lock', SiteLock);
}

